<template>
  <div class="content-mode-select">
    <!-- Select Button: Choose between 3 modes -->
    <SelectButton
      :modelValue="currentMode"
      :options="modes"
      optionLabel="label"
      optionValue="value"
      @update:model-value="onModeChange"
    >
      <template #option="slotProps">
        <div
          v-tooltip="getModeTooltip(slotProps.option.value)"
          style="display: flex; align-items: center; gap: 0.5rem"
        >
          <i :class="slotProps.option.icon" />
          <!-- <span>{{ t(getModeButtonKey(slotProps.option.value)) }}</span> -->
        </div>
      </template>
    </SelectButton>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import SelectButton from 'primevue/selectbutton';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';

import { useExternalWindow } from '@/composables/useExternalWindow';
import type { ContentMode } from '@/stores/contentmode';
import { useContentModeStore } from '@/stores/contentmode';
import { buildContentModeKey } from '@/utils/contentModeKey';

const route = useRoute();
const { t } = useI18n();
const contentModeStore = useContentModeStore();
const { openExternalWindow, closeExternalWindow } = useExternalWindow();

// Get tooltip message for each mode
const getModeTooltip = (mode: string) =>
  t(`titletags.contentMode${mode.charAt(0).toUpperCase() + mode.slice(1)}`);

// Available content modes (now with computed labels from i18n)
const modes = computed(() => [
  { value: 'standard', label: t('buttons.contentModeStandard'), icon: 'mdi mdi-fullscreen' },
  { value: 'split', label: t('buttons.contentModeSplit'), icon: 'mdi mdi-arrow-split-vertical' },
  /* external mode might open sidecar view in a separate window, which is critical due to security reasons; In this state of the application it is not implemented to the finish line due to that reason */
  // { value: 'external', label: t('buttons.contentModeExternal'), icon: 'mdi mdi-dock-window' },
]);

// Build route key from mainComponent + subComponent + subComponentIdentifier
const routeKey = computed(() => {
  const mainComponent = route.params.mainComponent as string;
  const subComponent = route.params.subComponent as string;
  const subComponentIdentifier = route.params.subComponentIdentifier as string;

  return buildContentModeKey({
    mainComponent,
    subComponent,
    subComponentIdentifier,
  });
});

// Get current mode from store (based on route key)
const currentMode = computed(() => {
  return contentModeStore.getContentModeForRoute(routeKey.value);
});

// Handle mode change
const onModeChange = (newMode: string) => {
  const mode = newMode as ContentMode;
  const previousMode = contentModeStore.getContentModeForRoute(routeKey.value);

  // When switching to 'external' → open window
  if (mode === 'external') {
    const externalUrl = `${window.location.origin}${route.path}?contentMode=external&windowType=external`;

    const externalWindow = openExternalWindow(routeKey.value, externalUrl, {
      width: 1000,
      height: 700,
      left: window.screenX + 100,
      top: window.screenY + 100,
    });

    if (!externalWindow) {
      console.warn('[ContentModeSelect] Failed to open external window - popup may be blocked');
      // Fallback: stay with previous mode
      return;
    }
  }
  // When switching away from 'external' → close window
  else if (previousMode === 'external') {
    closeExternalWindow(routeKey.value);
  }

  contentModeStore.setContentModeForRoute(routeKey.value, mode);
  contentModeStore.setActiveContentModeKey(routeKey.value); // ← IMPORTANT: Sync with DesktopLayout!
};
</script>

<style scoped>
.content-mode-select {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.debug-info {
  border: 1px solid #e0e0e0;
}
</style>
